import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Notification, NotificationFilter } from 'modules/core/domain/interfaces/supabase/notifications/types';
import { ApiError, toApiError } from 'shared/errors';
import {
  getGlobalLastReadNotificationId,
  getLastReadNotificationId,
  setGlobalLastReadNotificationId,
  setLastReadNotificationId,
} from '../domain/utils/notifications';
import {
  createNotification,
  deleteNotification,
  fetchNotificationById,
  fetchNotifications,
  isRangeNotSatisfiable,
  updateNotification,
} from './actions';

interface NotificationsState {
  items: Notification[];
  total: number;
  loading: boolean;
  /**
   * Отказ последней **загрузки** (список, одно уведомление): `ApiError` с кодом, по которому экран берёт причину
   * из реестра `shared/errors`. Отказы создания, правки и удаления сюда не пишутся — их показывает тост.
   */
  error: ApiError | null;
  currentNotification: Notification | null;
  filters: NotificationFilter;
  pageSize: number;
  hasMore: boolean;
  // Единая система хранения отметок прочитанного
  globalLastReadId: number | null;
  pageLastReadId: number | null;
}

const initialState: NotificationsState = {
  items: [],
  total: 0,
  loading: false,
  error: null,
  currentNotification: null,
  filters: {
    type: 'all',
    sort: 'desc',
    limit: 10,
    offset: 0,
  },
  pageSize: 10,
  hasMore: true,
  globalLastReadId: getGlobalLastReadNotificationId(),
  pageLastReadId: getLastReadNotificationId(),
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentNotification: (state) => {
      state.currentNotification = null;
    },
    setFilters: (state, action: PayloadAction<NotificationFilter>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Сбрасываем offset при изменении фильтров
      };
      state.items = []; // Очищаем список при изменении фильтров
      state.hasMore = true; // Сбрасываем hasMore при смене фильтров
    },
    setOffset: (state, action: PayloadAction<number>) => {
      state.filters.offset = action.payload;
    },
    // Единая система: управление отметками прочитанного
    setGlobalLastReadId: (state, action: PayloadAction<number>) => {
      state.globalLastReadId = state.globalLastReadId == null ? action.payload : Math.max(state.globalLastReadId, action.payload);
      setGlobalLastReadNotificationId(action.payload);
    },
    setPageLastReadId: (state, action: PayloadAction<number>) => {
      state.pageLastReadId = action.payload;
      setLastReadNotificationId(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        let newItems: Notification[];
        if (state.filters.offset === 0) {
          state.items = action.payload.notifications;
        } else {
          // Добавляем только уникальные по id
          const existingIds = new Set(state.items.map((n) => n.id));
          newItems = action.payload.notifications.filter((n) => !existingIds.has(n.id));
          state.items = [...state.items, ...newItems];
        }
        state.total = action.payload.total;
        // Принудительная сортировка по дате
        state.items.sort((a, b) => {
          const aDate = new Date(a.createdAt).getTime();
          const bDate = new Date(b.createdAt).getTime();
          return state.filters.sort === 'asc' ? aDate - bDate : bDate - aDate;
        });
        // Контроль hasMore
        const pageSize = state.filters.limit || state.pageSize;
        state.hasMore = action.payload.notifications.length >= pageSize;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        // Пустой диапазон страниц (PGRST103) — не ошибка: список просто закончился.
        if (!action.payload && isRangeNotSatisfiable(action.error)) {
          return;
        }
        state.error = toApiError(action.payload ?? action.error);
      })

      // Fetch single notification
      .addCase(fetchNotificationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationById.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.loading = false;
        state.currentNotification = action.payload;
      })
      .addCase(fetchNotificationById.rejected, (state, action) => {
        state.loading = false;
        state.error = toApiError(action.payload ?? action.error);
      })

      // Create notification
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotification.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.total += 1;
      })
      // Отказ мутации показывает тост на месте вызова; `state.error` — только про загрузку (чтения).
      .addCase(createNotification.rejected, (state) => {
        state.loading = false;
      })

      // Update notification
      .addCase(updateNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotification.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentNotification?.id === action.payload.id) {
          state.currentNotification = action.payload;
        }
      })
      // Отказ мутации показывает тост на месте вызова; `state.error` — только про загрузку (чтения).
      .addCase(updateNotification.rejected, (state) => {
        state.loading = false;
      })

      // Delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total -= 1;
        if (state.currentNotification?.id === action.payload) {
          state.currentNotification = null;
        }
      })
      // Отказ мутации показывает тост на месте вызова; `state.error` — только про загрузку (чтения).
      .addCase(deleteNotification.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearError, clearCurrentNotification, setFilters, setOffset, setGlobalLastReadId, setPageLastReadId } =
  notificationsSlice.actions;
export default notificationsSlice.reducer;
